import axios from 'axios';
import { sessionService } from '@/shared/services/session.service';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3009';

const leadsApi = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/agency/leads`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
leadsApi.interceptors.request.use(config => {
  config.withCredentials = true;
  const user = sessionService.getUser();
  if (config.headers && user?.tenantId) {
    config.headers['x-tenant-id'] = user.tenantId;
  }
  return config;
});

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  stage: 'new' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';
  source: 'website' | 'referral' | 'social' | 'advertising' | 'other';
  budget: number;
  assignedTo?: string;
  createdAt: Date;
  lastContact: Date;
  notes: string;
  propertyInterests?: string[];
  preferredLocation?: string;
  timeline?: string;
  communicationHistory?: Array<{
    id: string;
    type: 'call' | 'email' | 'meeting' | 'note';
    content: string;
    date: Date;
    agent: string;
  }>;
}

export interface LeadCreateDTO {
  name: string;
  email: string;
  phone: string;
  source: string;
  budget: number;
  notes?: string;
  propertyInterests?: string[];
  preferredLocation?: string;
  timeline?: string;
  assignedTo?: string;
}

export interface LeadUpdateDTO extends Partial<LeadCreateDTO> {
  id: string;
  stage?: string;
  lastContact?: Date;
}

export interface LeadFilters {
  stage?: string;
  source?: string;
  assignedTo?: string;
  budgetMin?: number;
  budgetMax?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface LeadStats {
  totalLeads: number;
  leadsByStage: Record<string, number>;
  leadsBySource: Record<string, number>;
  conversionRate: number;
  averageBudget: number;
  topPerformingAgent: string;
  monthlyGrowth: number;
}

export const leadsApiService = {
  // Obtener todos los leads con filtros
  async getLeads(filters: LeadFilters = {}): Promise<{
    leads: Lead[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();

    if (filters.stage) params.append('stage', filters.stage);
    if (filters.source) params.append('source', filters.source);
    if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters.budgetMin)
      params.append('budgetMin', filters.budgetMin.toString());
    if (filters.budgetMax)
      params.append('budgetMax', filters.budgetMax.toString());
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await leadsApi.get(`?${params.toString()}`);
    return response.data;
  },

  // Obtener un lead por ID
  async getLeadById(id: string): Promise<Lead> {
    const response = await leadsApi.get(`/${id}`);
    return response.data;
  },

  // Crear nuevo lead
  async createLead(leadData: LeadCreateDTO): Promise<Lead> {
    const response = await leadsApi.post('/', leadData);
    return response.data;
  },

  // Actualizar lead
  async updateLead(id: string, leadData: LeadUpdateDTO): Promise<Lead> {
    const response = await leadsApi.put(`/${id}`, leadData);
    return response.data;
  },

  // Eliminar lead
  async deleteLead(id: string): Promise<void> {
    await leadsApi.delete(`/${id}`);
  },

  // Obtener estadísticas de leads
  async getLeadStats(): Promise<LeadStats> {
    const response = await leadsApi.get('/stats');
    return response.data;
  },

  // Actualizar etapa del lead
  async updateLeadStage(id: string, stage: string): Promise<Lead> {
    const response = await leadsApi.patch(`/${id}/stage`, { stage });
    return response.data;
  },

  // Asignar lead a agente
  async assignLead(id: string, agentId: string): Promise<Lead> {
    const response = await leadsApi.patch(`/${id}/assign`, { agentId });
    return response.data;
  },

  // Agregar nota de comunicación
  async addCommunicationNote(
    id: string,
    noteData: {
      type: 'call' | 'email' | 'meeting' | 'note';
      content: string;
      agent: string;
    }
  ): Promise<Lead> {
    const response = await leadsApi.post(`/${id}/communication`, noteData);
    return response.data;
  },

  // Obtener historial de comunicación
  async getCommunicationHistory(id: string): Promise<{
    communications: Array<{
      id: string;
      type: 'call' | 'email' | 'meeting' | 'note';
      content: string;
      date: Date;
      agent: string;
    }>;
  }> {
    const response = await leadsApi.get(`/${id}/communication`);
    return response.data;
  },

  // Convertir lead en cliente
  async convertToClient(
    id: string,
    clientData: {
      name: string;
      email: string;
      phone: string;
      address?: string;
    }
  ): Promise<{
    lead: Lead;
    client: {
      id: string;
      name: string;
      email: string;
      phone: string;
    };
  }> {
    const response = await leadsApi.post(`/${id}/convert`, clientData);
    return response.data;
  },

  // Obtener leads por agente
  async getLeadsByAgent(agentId: string): Promise<Lead[]> {
    const response = await leadsApi.get(`/agent/${agentId}`);
    return response.data;
  },

  // Exportar leads
  async exportLeads(
    filters: LeadFilters,
    format: 'csv' | 'excel'
  ): Promise<Blob> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });
    params.append('format', format);

    const response = await leadsApi.get(`/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default leadsApiService;
